import React, { ReactElement } from 'react';
import {
  GetWorkerDataDocument,
  useGetWorkerAccessRestorePreviewQuery,
  useUpdateJobsiteWorkerMutation,
  WorkerAccessRestorePreviewStatus,
} from 'apollo/generated/client-operations';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { getGraphQLError } from 'utils/error';
import { AlertService } from 'components/alertService';
import { useIsMounted } from 'utils';
import { getCurrentISOFormattedDate } from 'utils/dates';
import {
  getDefaultValues,
  getFormInputs,
} from './RestoreWorkerAccessModalContent.forms';
import {
  AccessRestoringType,
  RestoreWorkerAccessFormData,
  RestoreWorkerAccessModalContentProps,
} from './types';
import { RestoreWorkerAccessPreview } from './RestoreWorkerAccessPreview';

export function RestoreWorkerAccessModalContent(
  props: RestoreWorkerAccessModalContentProps,
): ReactElement {
  const {
    closeModal,
    onConfirm,
    jobsiteWorker,
    accessRestoringType,
    workerId,
  } = props;
  const { jobsiteWorkerId } = jobsiteWorker ?? {};

  const isOnboarding = accessRestoringType === AccessRestoringType.Onboard;
  const isNotOnboarding = !isOnboarding;

  const isMounted = useIsMounted();
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  const siteId = jobsiteWorker?.jobsiteContractor.jobsite.jobsiteId;
  const {
    data: previewData,
    loading,
    error: previewError,
    refetch,
    startPolling,
    stopPolling,
  } = useGetWorkerAccessRestorePreviewQuery({
    variables: { siteId, workerId },
    skip: isOnboarding || !siteId || !workerId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const preview = previewData?.workerAccessRestorePreview;
  const isPreparing =
    preview?.status === WorkerAccessRestorePreviewStatus.Preparing;

  React.useEffect(() => {
    if (isNotOnboarding && isPreparing && !previewError) startPolling(2000);
    else stopPolling();
    return stopPolling;
  }, [isNotOnboarding, isPreparing, previewError, startPolling, stopPolling]);

  const retryPreview = (): void => {
    // Apollo exposes the error through the hook; keep the retry inside the modal.
    // The preview is informational, so a failure never blocks the restore.
    void refetch().catch((): void => undefined);
  };

  const [updateJobsiteWorker] = useUpdateJobsiteWorkerMutation({
    refetchQueries: [GetWorkerDataDocument],
  });

  const onSubmit: FormOnSubmit<RestoreWorkerAccessFormData> = async (
    data,
    event,
  ): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);

    const { category, approvedBy, reason } = data;

    try {
      await updateJobsiteWorker({
        variables: {
          jobsiteWorkerId,
          jobsiteWorkerInput: {
            ...(isOnboarding
              ? { endDate: null, startDate: getCurrentISOFormattedDate() }
              : {
                  siteAccessChangeApprovedBy: approvedBy,
                  isBanned: false,
                  banCategory: category.value,
                  banRemovedReason: reason,
                  bannedAt: null,
                  bannedReason: null,
                  banExpiresAt: null,
                }),
          },
        },
      });
      onConfirm();
    } catch (error) {
      event.preventDefault();
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  };

  const formInputs = React.useMemo(
    () => (isNotOnboarding ? getFormInputs() : null),
    [isNotOnboarding],
  );
  const defaultValues = React.useMemo(
    () => (isNotOnboarding ? getDefaultValues(jobsiteWorker) : null),
    [isNotOnboarding, jobsiteWorker],
  );

  return (
    <ModalFormContent
      setOpen={closeModal}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Restore Access"
      actionButtonWithSpinner={isSaving}
      actionButtonEnabled={!isSaving}
      renderAbove={
        isNotOnboarding ? (
          <RestoreWorkerAccessPreview
            loading={loading}
            failed={!!previewError}
            preview={preview}
            onRetry={retryPreview}
          />
        ) : undefined
      }
    />
  );
}
